import { useEffect, useState } from 'react';
import useI18n from '../../../../hooks/useI18n';
import { useLocaleFormat } from '../../../../hooks/useLocaleFormat';

// Screen readers drop polite messages that arrive while the user is typing,
// because the key echo interrupts them. Waiting for a pause is what makes the
// result reliably heard. The delay is taken from GOV.UK's accessible
// autocomplete, which was tested with screen reader users.
export const SEARCH_STATUS_DELAY_MS = 1400;

type SearchStatusProps = {
  /** The current search. Every change schedules a new announcement. */
  query: string;
  /** Rows matching the search. */
  resultCount: number;
};

/**
 * Announces search results to screen reader users (WCAG 4.1.3).
 *
 * Two regions take turns, so a message equal to the previous one ("1 result"
 * after typing one more letter) is still a change and is read again. Both are
 * rendered from the start: a live region added together with its text is not
 * announced by every browser.
 */
export function SearchStatus({ query, resultCount }: SearchStatusProps) {
  const { t } = useI18n();
  const { formatNumber } = useLocaleFormat();
  const [announcement, setAnnouncement] = useState({
    text: '',
    useFirstRegion: false,
  });

  // Empty without a search, so nothing is announced on page load.
  // `count` picks the plural form, `formattedCount` is what is read out.
  const message = !query
    ? ''
    : resultCount
      ? t('searchResultsCount', {
          count: resultCount,
          formattedCount: formatNumber(resultCount),
        })
      : t('noResultsFound');

  useEffect(() => {
    const timeout = setTimeout(() => {
      setAnnouncement((previous) => ({
        text: message,
        useFirstRegion: !previous.useFirstRegion,
      }));
    }, SEARCH_STATUS_DELAY_MS);

    return () => clearTimeout(timeout);
  }, [query, message]);

  return (
    <>
      <span role='status' aria-atomic='true' className='eye:sr-only'>
        {announcement.useFirstRegion ? announcement.text : ''}
      </span>
      <span role='status' aria-atomic='true' className='eye:sr-only'>
        {announcement.useFirstRegion ? '' : announcement.text}
      </span>
    </>
  );
}
